// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { selectAnno, selectEnte } from '@ideapa/app-auth';
import { concatReducers } from '@ideapa/app-common';
import { BaseDto } from '@ideapa/common-dto';
import { EntityAdapter } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { SharedEntityActions, SharedEntityState } from './models';

export function createReducerFactory<T extends BaseDto>() {
  function extendBaseReducer<V extends SharedEntityState<T>>(
    initialState: V,
    actions: SharedEntityActions<T>,
    entityAdapter: EntityAdapter<T>,
    reducerExtension: ActionReducer<V, Action>,
    resetOnEnteAnnoChange = false
  ) {
    const resetByEnteAnnoReducerExtension = createReducer(
      initialState,
      on(selectAnno, (state, { anno, prevAnno }) => {
        if (anno === prevAnno) {
          return state;
        } else {
          return entityAdapter.removeAll({
            ...state,
            loadedAll: false,
          });
        }
      }),
      on(selectEnte, (state, { ente, prevEnte }) => {
        if (ente === prevEnte) {
          return state;
        } else {
          return entityAdapter.removeAll({
            ...state,
            loadedAll: false,
          });
        }
      })
    );

    const basicReducer = createReducer<V>(
      initialState,
      on(
        actions.addFailure,
        actions.deleteEntityFailure,
        actions.updateFailure,
        actions.loadAllFailure,
        actions.loadPagedFailure,
        (s, a) => ({ ...s, error: a.error })
      ),
      on(actions.loadPaged, (state) => ({
        ...state,
        error: undefined,
        loadedAll: false,
      })),
      on(actions.loadPagedSuccess, (state, { items }) =>
        entityAdapter.setMany(items, {
          ...state,
          loadedAll: false,
          error: undefined,
        })
      ),
      on(actions.loadAll, (state) => ({
        ...state,
        error: undefined,
        loadedAll: false,
      })),
      on(actions.loadAllSuccess, (state, { items }) =>
        entityAdapter.setAll(items, {
          ...state,
          loadedAll: true,
          error: undefined,
        })
      ),
      on(actions.loadSingleSuccess, (state, { item }) =>
        entityAdapter.setOne(item, state)
      ),
      on(actions.updateSuccess, (state, action) =>
        entityAdapter.updateOne(
          { id: action.item.id, changes: action.item },
          state
        )
      ),
      on(actions.updateManySuccess, (state, action) =>
        entityAdapter.updateMany(
          action.items.map((item: any) => ({
            id: item.id,
            changes: item,
          })),
          state
        )
      ),
      on(actions.addSuccess, (state, action) =>
        entityAdapter.addOne(action.item, state)
      ),
      on(actions.deleteEntitySuccess, (state, action) =>
        entityAdapter.removeOne(action.id, state)
      )
    );

    const reducers = [basicReducer, reducerExtension];
    if (resetOnEnteAnnoChange) reducers.push(resetByEnteAnnoReducerExtension);

    return concatReducers(...reducers);
  }

  return {
    extendBaseReducer,
  };
}
